/**
 * Fuzzy + exact matching primitives — used by the ranker AND by sources that
 * back themselves with in-memory iteration.
 *
 * Two functions:
 *
 *   - levenshtein(a, b) — classic edit distance, capped at `maxDistance` for
 *     early bailout (we only care whether the distance is "small enough" for
 *     fuzzy match, not the exact value at large distances)
 *   - fuzzyScore(query, candidate) — normalized 0..1 score combining
 *     prefix-match boost, contains-match, and edit-distance similarity
 *
 * Determinism: every input pair always returns the same score. No Date.now,
 * no randomness, no mutable global state.
 */
/**
 * Levenshtein distance with early bailout.
 *
 * Cap `maxDistance` to keep cost O(min(|a|,|b|)*maxDistance) instead of
 * O(|a|*|b|). For fuzzy retrieval at typical thresholds (≤2 edits) this is
 * effectively O(n).
 */
export function levenshtein(a, b, maxDistance = 4) {
    if (a === b)
        return 0;
    if (a.length === 0)
        return b.length;
    if (b.length === 0)
        return a.length;
    // Length difference alone exceeds budget — bail immediately.
    if (Math.abs(a.length - b.length) > maxDistance)
        return maxDistance + 1;
    // Two-row dynamic programming table.
    const m = a.length;
    const n = b.length;
    let prev = new Array(n + 1);
    let curr = new Array(n + 1);
    for (let j = 0; j <= n; j++)
        prev[j] = j;
    for (let i = 1; i <= m; i++) {
        curr[0] = i;
        let rowMin = i;
        for (let j = 1; j <= n; j++) {
            const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
            curr[j] = Math.min(prev[j] + 1, // deletion
            curr[j - 1] + 1, // insertion
            prev[j - 1] + cost // substitution
            );
            if (curr[j] < rowMin)
                rowMin = curr[j];
        }
        // Early exit: if the entire row is already over budget, no path forward
        // can make it back under.
        if (rowMin > maxDistance)
            return maxDistance + 1;
        [prev, curr] = [curr, prev];
    }
    return prev[n];
}
/**
 * Fuzzy score — normalized 0..1 similarity between query and candidate.
 *
 *   exact match           → 1.00
 *   case-insensitive eq   → 0.98
 *   prefix match          → 0.85 — 0.97 (longer prefix → higher)
 *   substring match       → 0.65 — 0.85 (shorter substring → higher relative weight)
 *   edit-distance ≤ 2     → 0.40 — 0.70 (closer → higher)
 *   distance > 2          → 0.00
 */
export function fuzzyScore(query, candidate) {
    if (query === "" || candidate === "")
        return 0;
    if (query === candidate)
        return 1.0;
    const q = query.toLowerCase();
    const c = candidate.toLowerCase();
    if (q === c)
        return 0.98;
    // Prefix match: longer prefix relative to candidate length → stronger signal.
    if (c.startsWith(q)) {
        const ratio = q.length / c.length;
        return 0.85 + 0.12 * ratio; // 0.85 — 0.97
    }
    // Substring match (not at prefix).
    if (c.includes(q)) {
        const ratio = q.length / c.length;
        return 0.65 + 0.20 * ratio; // 0.65 — 0.85
    }
    // Edit distance fallback. Use a budget proportional to query length.
    const budget = Math.min(2, Math.floor(q.length / 3) + 1);
    const dist = levenshtein(q, c, budget);
    if (dist <= budget) {
        // Linear: dist=0 → 0.70, dist=budget → 0.40
        if (budget === 0)
            return 0.7;
        return 0.7 - (0.3 * dist) / budget;
    }
    return 0;
}
/** Case-insensitive substring containment. */
export function containsCI(haystack, needle) {
    if (needle === "")
        return true;
    return haystack.toLowerCase().includes(needle.toLowerCase());
}
//# sourceMappingURL=match.js.map